import { Box } from '@material-ui/core';
import { ReactNode } from 'react';

import { CustomFormLabel } from '@/components/shared/components/NewContextForms/shared/CustomFormLabel';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export interface CustomTextFieldLabelProps {
  label?: string | ReactNode;
  labelDynamicDataTexts?: string[];
  labelIcon?: string;
  labelIconPosition?: 'start' | 'end';
  tooltipTitle?: string | number | JSX.Element | null;
  strongTitle?: boolean;
  forceSingleLine?: boolean;
  markAsOptional?: boolean;
  disableTranslation?: boolean;
  labelSuffix?: ReactNode;
  children?: ReactNode;
  highlightLabel?: boolean;
}

export const CustomTextFieldLabel = ({
  label,
  labelDynamicDataTexts,
  tooltipTitle,
  labelIcon = tooltipTitle ? 'info' : undefined,
  labelIconPosition = 'end',
  strongTitle,
  markAsOptional,
  disableTranslation,
  labelSuffix,
  children,
  highlightLabel = false,
}: CustomTextFieldLabelProps) => {
  const { palette } = useCustomTheme();

  const isStringLabel = typeof label === 'string';

  if (!label && !labelIcon) return null;

  const icon = labelIcon ? (
    <Box
      component="span"
      display="inline-flex"
      style={{
        pointerEvents: 'auto',
        cursor: tooltipTitle ? 'help' : undefined,
        lineHeight: 1,
        paddingLeft: labelIconPosition === 'end' ? 6 : 0,
        paddingRight: labelIconPosition === 'start' ? 6 : 0,
        flexShrink: 0,
      }}
    >
      <i className="material-icons-outlined" style={{ fontSize: 16, color: palette.grey[400], lineHeight: 1 }}>
        {labelIcon}
      </i>
    </Box>
  ) : null;

  const iconWithTooltip =
    tooltipTitle && icon ? (
      <StyledTooltip title={tooltipTitle as string} tooltipProps={{ placement: 'right' }}>
        {icon}
      </StyledTooltip>
    ) : (
      icon
    );

  return (
    <Box display="inline-flex" alignItems="center" flexWrap="nowrap" maxWidth="100%" minWidth={0} pr={1}>
      <Box
        component="span"
        display="inline-flex"
        alignItems="center"
        minWidth={0}
        overflow={labelSuffix ? 'hidden' : undefined}
        flex={labelSuffix ? '0 1 auto' : undefined}
      >
        {labelIconPosition === 'start' && iconWithTooltip}
        {label &&
          (isStringLabel ? (
            <CustomFormLabel
              label={label}
              strongTitle={strongTitle}
              labelDynamicDataTexts={labelDynamicDataTexts ?? []}
              disableTranslation={disableTranslation}
              color={highlightLabel ? 'textPrimary' : 'textSecondary'}
            />
          ) : (
            label
          ))}
        {children}
      </Box>
      {labelSuffix}
      {labelIconPosition === 'end' && iconWithTooltip}
      {markAsOptional && (
        <div style={{ opacity: 0.5, paddingLeft: '0.5em' }}>
          {'('}
          <Text tId={'OPTIONAL'} />
          {')'}
        </div>
      )}
    </Box>
  );
};
